<script setup lang="ts">
    import { cropImage } from '@/helpers/cropper';
    import { injectChatbotStyles } from '@/helpers/inject-external-styles';
    import type { WidgetProps } from '@/typings/widget';
    import { getPendingMessage, removePendingMessage } from '@/utils/pending-message-storage';
    import { createChat } from '@chatbot/components/Chat';
    import type { ChatInstance, ChatOptions } from '@chatbot/typings/types';
    import { extractChatStyles } from '@chatbot/utils/component-styles';
    import Icon from '@shared/components/ui/icon/icon.vue';

    const { t } = useI18n();

    const emit = defineEmits<{(event: 'back' | 'scrollToTop')}>();

    const { venue } = storeToRefs(useWidgetVenueStore());

    const widgetProps = inject<WidgetProps>('widgetProps');

    // Ref for the chat container element
    const chatContainerRef = ref<HTMLElement | null>(null);

    // Store chat instance and style element
    let chatInstance: ChatInstance | null = null;
    let chatStyleElement: HTMLStyleElement | null = null;

    /**
     * Check for and send any pending message from welcome screen
     */
    const checkAndSendPendingMessage = async(): Promise<void> => {
        const pendingMessageResult = getPendingMessage();

        if (pendingMessageResult.exists && pendingMessageResult.message && chatInstance) {
            try {
                // Send the pending message
                await chatInstance.sendPendingMessage(pendingMessageResult.message.message);

                // Clear from storage after successful send
                removePendingMessage();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error sending pending message:', error);

                // Note: Message stays in storage for potential retry
            }
        }
    };

    // Initialize chat when component mounts
    onMounted(async() => {
        if (!chatContainerRef.value) return;

        // STEP 1: Inject chatbot styles into shadow DOM
        chatStyleElement = injectChatbotStyles(chatContainerRef.value, extractChatStyles);

        // STEP 2: Create chat options
        const chatOptions: ChatOptions = {
            target: chatContainerRef.value,
            venueId: String(widgetProps?.venue),
            venueSettingsUrl: `${import.meta.env.VITE_API_BASE_URL}/api/v1/public/widget/venue/show`,
            title: venue.value?.venueManager.displayName,
            n8nWebhookChatUrl: String(import.meta.env.VITE_N8N_WEBHOOK_CHAT_URL),
            mode: 'fixed',
            maxWidth: '100%',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            avatarUrl: venue.value?.venueManager?.profileImage?.fullImagePath ? cropImage(venue.value?.venueManager?.profileImage?.fullImagePath, 200) : '',
            theme: { primaryColor: widgetProps?.brandColor },
            welcomeMessage: t('widget.chat.customWelcomeMessage'),
            agent: venue.value?.allStarsQuoteEnabled ? 'ALL_STARS' : 'STARLIGHT',
            environment: String(import.meta.env.VITE_ENVIRONMENT ?? 'development'),
        };

        // STEP 3: Create chat instance
        chatInstance = createChat(chatOptions);

        // STEP 4: Check for pending message after next tick
        // This ensures chat is fully initialized
        await nextTick();
        await checkAndSendPendingMessage();

        chatInstance.setLocale(widgetProps?.locale ?? 'nl');
    });

    // Cleanup when component unmounts
    onBeforeUnmount(() => {
        if (chatInstance) {
            chatInstance.destroy();
            chatInstance = null;
        }

        // Clean up injected styles
        if (chatStyleElement) {
            chatStyleElement.remove();
            chatStyleElement = null;
        }
    });
</script>

<template>
    <div class="chatbox">
        <div class="chatbox__header">
            <button
                class="chatbox__back btn btn--icon btn btn--ghost btn--thin"
                type="button"
                @click="emit('back')"
            >
                <Icon
                    name="chevronLeft"
                    size="medium"
                />
            </button>
        </div>
        <div
            ref="chatContainerRef"
            class="chatbox__content"
        />
    </div>
</template>

<style lang="scss" scoped>
.chatbox {
    display: flex;
    flex-direction: column;
    block-size: 100%;
    inline-size: 100%;

    &__header {
        position: relative;
        display: flex;
        align-items: center;
    }

    &__content {
        overflow: hidden; // Let chat handle scrolling
        flex-grow: 1;
        padding-block-end: var(--spacing-04);
        padding-inline: var(--spacing-04);

        :deep(.chat-wrapper) {
            overflow: hidden;
            border-radius: var(--border-radius-md);
            block-size: 100%;
        }
    }
}
</style>
